import {zodResolver} from '@hookform/resolvers/zod';
import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {graphqlClient} from '@/services/graphqlClient';
import type {JobRole} from '@/services/graphqlHooks';
import {
  useDeleteJobRoleMutation,
  useGetJobRolesQuery,
  useUpdateJobRoleMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {JobRoleFormValues} from '../utils/jobRoleFormInputs';
import {
  jobRoleFormDefaultValues,
  jobRoleFormSchema,
  mapJobRoleFormValuesToUpdateJobRoleInput,
} from '../utils/jobRoleFormInputs';
import JobRoleFormFields from './JobRoleFormFields';

export type EditJobRoleDialogProps = DialogProps & {
  jobRole?: JobRole;
};

const EditJobRoleDialog = ({
  jobRole,
  onOpenChange,
  ...props
}: EditJobRoleDialogProps) => {
  const {t} = useTranslation();
  const {confirm} = useConfirm();
  const {toast} = useToast();

  const updateJobRole = useUpdateJobRoleMutation(graphqlClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: useGetJobRolesQuery.getKey()});
    },
  });
  const deleteJobRole = useDeleteJobRoleMutation(graphqlClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: useGetJobRolesQuery.getKey()});
    },
  });

  const isDisabled = updateJobRole.isPending || deleteJobRole.isPending;

  const form = useForm<JobRoleFormValues>({
    resolver: zodResolver(jobRoleFormSchema),
    defaultValues: jobRoleFormDefaultValues(jobRole),
  });

  useEffect(() => {
    if (!jobRole) return;
    form.reset(jobRoleFormDefaultValues(jobRole));
  }, [form, jobRole]);

  const handleSubmit = async (values: JobRoleFormValues) => {
    if (!jobRole) return;
    try {
      await updateJobRole.mutateAsync({
        input: mapJobRoleFormValuesToUpdateJobRoleInput(jobRole.id, values),
      });
      toast({title: t('Job role updated'), variant: 'success'});
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!jobRole) return;
    try {
      await confirm({
        title: t('Delete job role'),
        message: t('Are you sure you want to delete this job role?'),
      });
      await deleteJobRole.mutateAsync({id: jobRole.id});
      toast({title: t('Job role deleted'), variant: 'success'});
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Edit job role')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              {jobRole && <JobRoleFormFields form={form} />}
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                className="sm:mr-auto"
                variant="warning"
                onClick={handleDelete}
                disabled={isDisabled}
              >
                {t('Delete job role')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange?.(false)}
              >
                {t('Cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={isDisabled}>
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default EditJobRoleDialog;
